import { API_BASE_URL, transactionsAPI } from '../services/api';

/**
 * Fetch transaction receipt preview HTML from backend
 * @param {number} transactionId - Transaction ID
 * @returns {Promise<{ html: string, transaction: Object }>} Preview data
 */
export const fetchReceiptPreview = async (transactionId) => {
  const response = await transactionsAPI.previewReceipt(transactionId);
  return response.data; // { html, transaction }
};

/**
 * Download transaction receipt as PDF
 * @param {Object} transaction - Transaction object with id
 * @param {Function} showLoading - Function to show loading state
 * @param {Function} hideLoading - Function to hide loading state
 * @param {Function} showSuccess - Function to show success message
 * @param {Function} showError - Function to show error message
 */
export const downloadTransactionReceipt = async (
  transaction,
  { showLoading, hideLoading, showSuccess, showError },
) => {
  try {
    showLoading();

    // Make the API call
    const response = await fetch(`${API_BASE_URL}/transactions/${transaction.id}/receipt-pdf`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Get the blob
    const blob = await response.blob();

    // Check if we actually got a PDF blob
    if (!blob || blob.size === 0) {
      throw new Error('Empty PDF received');
    }

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `receipt-${transaction.receipt_number}.pdf`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);

    showSuccess('Receipt downloaded successfully');
  } catch (error) {
    console.error('PDF download error:', error);
    const errorMessage = error?.message || 'Failed to download receipt';
    showError(errorMessage);
  } finally {
    hideLoading();
  }
};

/**
 * Send transaction receipt via email
 * @param {Object} transaction - Transaction object with id
 * @param {Function} showLoading - Function to show loading state
 * @param {Function} hideLoading - Function to hide loading state
 * @param {Function} showSuccess - Function to show success message
 * @param {Function} showError - Function to show error message
 */
export const sendTransactionReceiptEmail = async (
  transaction,
  { showLoading, hideLoading, showSuccess, showError },
) => {
  try {
    showLoading();
    await transactionsAPI.sendReceiptEmail(transaction.id);
    showSuccess('Receipt sent via email successfully');
  } catch (error) {
    console.error('Email send error:', error);
    showError(error?.error?.message || 'Failed to send receipt via email');
  } finally {
    hideLoading();
  }
};

/**
 * Send transaction receipt via WhatsApp
 * @param {Object} transaction - Transaction object with id
 * @param {Function} showLoading - Function to show loading state
 * @param {Function} hideLoading - Function to hide loading state
 * @param {Function} showSuccess - Function to show success message
 * @param {Function} showError - Function to show error message
 */
export const sendTransactionReceiptWhatsApp = async (
  transaction,
  { showLoading, hideLoading, showSuccess, showError },
) => {
  try {
    showLoading();
    await transactionsAPI.sendReceiptWhatsApp(transaction.id);
    showSuccess('Receipt sent via WhatsApp successfully');
  } catch (error) {
    console.error('WhatsApp send error:', error);
    showError(error?.error?.message || 'Failed to send receipt via WhatsApp');
  } finally {
    hideLoading();
  }
};
